<template>
<div id="app" class="legal-page page-template-default no-sidebar separate-containers">
  <HeadNav selectedLanguage="en" :page="pageKey" :gotodownload="goToDownload" />

  <main class="legal-main">
    <article class="legal-shell">
      <header>
        <p class="eyebrow">{{ current.eyebrow }}</p>
        <h1>{{ current.title }}</h1>
        <p v-if="current.updated" class="updated">Effective Date: {{ current.updated }}</p>
      </header>

      <p v-if="current.intro" class="legal-intro">{{ current.intro }}</p>

      <section v-for="section in current.sections" :key="section.heading" class="legal-section">
        <h2>{{ section.heading }}</h2>
        <p v-for="paragraph in section.paragraphs" :key="paragraph">{{ paragraph }}</p>
        <ul v-if="section.items" class="legal-list">
          <li v-for="item in section.items" :key="item">{{ item }}</li>
        </ul>
        <p v-if="section.email && section.emailFirst" class="legal-email">
          <strong>Email:</strong>
          <a :href="`mailto:${section.email}`">{{ section.email }}</a>
        </p>
        <p v-for="paragraph in section.afterEmail" :key="paragraph">{{ paragraph }}</p>
        <p v-if="section.email && !section.emailFirst" class="legal-email">
          <strong>Email:</strong>
          <a :href="`mailto:${section.email}`">{{ section.email }}</a>
        </p>
      </section>
    </article>
  </main>
</div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';

const DEFAULT_DOMAIN = 'gbwtsapp.com';
const DEFAULT_APP_NAME = 'GB_gbwtsapp.com';

const pages = {
  contact: {
    title: 'Contact Us',
    eyebrow: 'Support',
    updated: '',
    intro:
      'If you have any questions, feedback, or suggestions about GB WhatsApp, you can contact us through the email address below.',
    sections: [
      {
        heading: 'Website Feedback',
        paragraphs: [
          'We welcome feedback about the website, including its content, navigation, design, and overall user experience.',
        ],
      },
      {
        heading: 'Content Corrections',
        paragraphs: [
          'If you find information that is outdated, inaccurate, or missing, please let us know. We will review the information and make updates when necessary.',
        ],
      },
      {
        heading: 'Technical Issues',
        paragraphs: [
          'If you experience a problem with a download page, broken link, or another technical issue on our website, you can contact us with the details.',
        ],
      },
      {
        heading: 'Contact Email',
        paragraphs: [],
        email: 'info@gbwtsapp.com',
        emailFirst: true,
        afterEmail: [
          'When contacting us, please include enough information about your question or issue so we can understand and respond appropriately.',
          'We aim to review messages and respond as soon as possible.',
        ],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    eyebrow: 'Site policy',
    updated: '',
    intro:
      'At GB WhatsApp, we respect your privacy and are committed to protecting information related to visitors to our website. This Privacy Policy explains what information may be collected when you use our website and how it may be used.',
    sections: [
      {
        heading: 'Information We Collect',
        paragraphs: [
          'We do not require visitors to create an account or provide personal information simply to browse our website.',
          'However, certain information may be collected automatically when you visit the site, such as your browser type, device information, IP address, pages visited, and general usage data.',
        ],
      },
      {
        heading: 'How We Use Information',
        paragraphs: [
          'Information collected through the website may be used to:',
        ],
        items: [
          'Maintain and improve the website.',
          'Understand how visitors use our content.',
          'Fix technical problems.',
          'Improve website performance and user experience.',
          'Display relevant advertising where applicable.',
        ],
      },
      {
        heading: 'Cookies',
        paragraphs: [
          'GB WhatsApp may use cookies and similar technologies to improve website functionality, understand traffic, and support advertising services.',
          'You can control or disable cookies through your browser settings. Some website functions may not work as expected when cookies are disabled.',
        ],
      },
      {
        heading: 'Third-Party Services',
        paragraphs: [
          'We may use third-party services such as analytics providers, advertising networks, or other website tools. These services may collect information according to their own privacy policies.',
          'We do not control how third-party services process information collected through their platforms.',
        ],
      },
      {
        heading: 'External Links',
        paragraphs: [
          'Our website may contain links to external websites. We are not responsible for the privacy practices, content, or security of third-party websites.',
          'We recommend reviewing the privacy policy of any external website you visit.',
        ],
      },
      {
        heading: "Children's Privacy",
        paragraphs: [
          'Our website is not specifically directed toward children, and we do not knowingly collect personal information from children.',
        ],
      },
      {
        heading: 'Changes to This Policy',
        paragraphs: [
          'We may update this Privacy Policy when necessary to reflect changes to our website, services, or legal requirements. Any updated version will be published on this page.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: ['If you have questions about this Privacy Policy, please contact us:'],
        email: 'info@gbwtsapp.com',
      },
    ],
  },
  terms: {
    title: 'Terms of Use',
    eyebrow: 'Legal terms',
    updated: '',
    intro:
      'By accessing and using GB WhatsApp, you agree to follow these Terms of Use. If you do not agree with these terms, please stop using the website.',
    sections: [
      {
        heading: 'Website Content',
        paragraphs: [
          'The information provided on GB WhatsApp is intended for general informational purposes. We make reasonable efforts to keep the content useful and up to date, but we do not guarantee that all information will always be complete, accurate, or current.',
        ],
      },
      {
        heading: 'APK Downloads',
        paragraphs: [
          'GB WhatsApp may provide information and links related to APK files and third-party applications. Users are responsible for checking compatibility, installation requirements, and the risks associated with installing third-party software.',
          'We recommend downloading files carefully and checking them before installation.',
        ],
      },
      {
        heading: 'Third-Party Applications',
        paragraphs: [
          'GB WhatsApp is a website providing information about a third-party modified application. We are not the official developer or publisher of WhatsApp.',
          'WhatsApp and its related trademarks belong to their respective owners.',
        ],
      },
      {
        heading: 'External Links',
        paragraphs: [
          'Our website may contain links to third-party websites. These links are provided for convenience or additional information. We do not control or guarantee the content, availability, or security of external websites.',
        ],
      },
      {
        heading: 'User Responsibility',
        paragraphs: [
          'Users are responsible for how they use information obtained from this website. You should comply with applicable laws, regulations, and the terms of service of any application or platform you use.',
        ],
      },
      {
        heading: 'Limitation of Liability',
        paragraphs: [
          'GB WhatsApp is not responsible for losses, damages, account restrictions, data loss, device problems, or other issues resulting from the use of third-party applications or information obtained through this website.',
        ],
      },
      {
        heading: 'Changes to These Terms',
        paragraphs: [
          'We may update these Terms of Use from time to time. Changes will become effective when the updated terms are published on this page.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: ['If you have questions about these Terms of Use, contact us at:'],
        email: 'info@gbwtsapp.com',
      },
    ],
  },
  disclaimer: {
    title: 'Disclaimer',
    eyebrow: 'Important notice',
    updated: '',
    intro:
      'The information provided on GB WhatsApp is for general informational purposes only. We make reasonable efforts to provide useful and updated information, but we do not guarantee that all information on this website is completely accurate, current, or error-free.',
    sections: [
      {
        heading: 'General Information',
        paragraphs: [
          'The information provided on GB WhatsApp is for general informational purposes only. We make reasonable efforts to provide useful and updated information, but we do not guarantee that all information on this website is completely accurate, current, or error-free.',
        ],
      },
      {
        heading: 'Third-Party Application',
        paragraphs: [
          'GB WhatsApp is a third-party modified application. It is not developed, operated, or officially supported by WhatsApp or Meta.',
          'We do not claim to be affiliated with, sponsored by, or endorsed by WhatsApp or Meta.',
        ],
      },
      {
        heading: 'APK Files and Downloads',
        paragraphs: [
          'Users should understand the risks associated with downloading and installing third-party APK files. Before installing any APK, check the source, file information, compatibility, and security of the file.',
          'Users are responsible for deciding whether to install or use third-party software on their devices.',
        ],
      },
      {
        heading: 'Account Restrictions',
        paragraphs: [
          'Using an unofficial modified WhatsApp client may carry a risk of account restrictions. We cannot guarantee that an account will remain active or that any anti-ban feature will prevent restrictions.',
        ],
      },
      {
        heading: 'Trademarks',
        paragraphs: [
          'WhatsApp, its logo, and related names are trademarks of their respective owners. Any trademarks mentioned on this website are used for identification and informational purposes only.',
        ],
      },
      {
        heading: 'External Websites',
        paragraphs: [
          'GB WhatsApp may link to external websites that we do not own or control. We are not responsible for the content, privacy policies, security, or availability of these external websites.',
        ],
      },
      {
        heading: 'No Guarantee',
        paragraphs: [
          'We do not guarantee that the website, downloads, links, or information provided will always be available, error-free, or suitable for every device or user.',
          'By using this website, you acknowledge these limitations and accept responsibility for your own decisions regarding third-party applications and downloads.',
        ],
      },
      {
        heading: 'Contact',
        paragraphs: ['For questions about this Disclaimer, please contact:'],
        email: 'info@gbwtsapp.com',
      },
    ],
  },
};

export default {
  name: 'LegalPage',
  components: {
    HeadNav,
  },
  data() {
    return {
      apk: null,
      from: 'gb',
      filename: 'gb',
    };
  },
  computed: {
    pageKey() {
      return this.$route.meta.legalKey || 'privacy';
    },
    current() {
      return pages[this.pageKey] || pages.privacy;
    },
  },
  mounted() {
    this.from = this.$global.GetQueryString('from') || this.from;
    this.filename = this.$global.GetQueryString('filename') || this.filename;
    if (this.$global.GetQueryString('from')) {
      this.getAPK();
    } else {
      this.getOfficalApk();
    }
  },
  methods: {
    getAPK() {
      this.$server
        .getAPPInfo({
          promotionChannel: this.from,
          promotionName: this.filename,
        })
        .then((res) => {
          this.apk = res.code == 200 ? res.data.apk : process.env.VUE_APP_GBLINK;
        });
    },
    getOfficalApk(type = 1, domain = null, appname = null) {
      this.$server
        .getOfficalApk({
          domain: domain || DEFAULT_DOMAIN,
          appName: appname || DEFAULT_APP_NAME,
        })
        .then((res) => {
          if (res.code == 200 && type == 1) {
            this.apk = res.data.apk;
          }
        });
    },
    goToDownload() {
      this.$router.push({
        name: 'en-download',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.legal-main {
  min-height: 72vh;
  background: #f6f8f7;
  padding: 42px 0 64px;
}

.legal-shell {
  width: min(860px, calc(100% - 32px));
  margin: 0 auto;
  padding: 34px;
  border: 1px solid #dbe8e1;
  border-radius: 8px;
  background: #fff;
}

.eyebrow {
  margin: 0 0 8px;
  color: #107b59;
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
}

h1 {
  margin: 0 0 10px;
  color: #10261d;
  font-size: 40px;
  font-weight: 800;
  line-height: 1.14;
  text-align: center;
}

.updated {
  color: #6c7f77;
}

.legal-section {
  padding-top: 24px;
  border-top: 1px solid #e2ece7;
}

h2 {
  margin: 0 0 12px;
  color: #15372a;
  font-size: 24px;
  font-weight: 800;
  text-align: center;
}

p,
li {
  color: #40544c;
  font-size: 16px;
  line-height: 1.72;
}

ul {
  margin: 0 0 16px 20px !important;
}

.legal-email a {
  color: #107b59;
  font-weight: 700;
  text-decoration: none;
}

.legal-email a:hover {
  text-decoration: underline;
}

@media (max-width: 620px) {
  .legal-main {
    padding: 24px 0 44px;
  }

  .legal-shell {
    width: min(100% - 24px, 860px);
    padding: 22px;
  }

  h1 {
    font-size: 32px !important;
  }
}
</style>
